import {type LinkBaseProps, cn} from '@tempmee/core'
import {type AnchorHTMLAttributes, forwardRef} from 'react'
import {linkVariants} from './Variants'

export interface LinkProps
  extends LinkBaseProps,
    // `LinkBaseProps` owns `color` and `children`; the anchor supplies the rest
    // (`href`, `target`, `onClick`, …). `color` is also the deprecated HTML attr.
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'children'> {
  /**
   * Render as a different element (MUI's `Link` `component`, and the same `as`
   * escape hatch `Box`/`Typography` carry). @default 'a'
   *
   * Use `as="button"` when the link performs an in-page action rather than
   * navigating — a bare `<a>` with no `href` is neither focusable nor
   * keyboard-operable, so a link-styled control with only an `onClick` must be a
   * real `<button>`. It defaults to `type="button"` so it never submits a form.
   */
  as?: 'a' | 'button'
}

/**
 * Text hyperlink following MUI's `Link` contract. Renders a semantic `<a>` and
 * exposes `color` (`primary` | `inherit`) and `underline` (`always` | `hover` |
 * `none`). Inherits font size from its surrounding text so it blends into body
 * copy; pass `href` (and any anchor attributes) as normal.
 *
 * For a link-looking control that acts instead of navigating, pass
 * `as="button"` — see the `as` prop.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({as = 'a', className, color = 'primary', underline = 'always', children, ...props}, ref) => {
    const isButton = as === 'button'
    // Narrowed to `'a'` for JSX's sake: a `'a' | 'button'` element type makes TS
    // intersect the two elements' props and refs, which nothing can satisfy.
    // The public contract is the anchor's on both branches anyway — that is what
    // `LinkProps` extends — so this loses no type safety the props had.
    const Component = as as 'a'

    return (
      <Component
        ref={ref}
        // Tailwind's preflight (shipped in this package's `styles.css`) already
        // makes a `<button>` inherit the surrounding font and drops its
        // background/border/padding. It does not touch `text-align`, which the
        // UA sets to `center` — so a multi-line link-button's label would come
        // out centred. The rest is restated because it cannot break inheritance
        // and keeps the reset intact for a consumer that skips preflight.
        className={cn(
          linkVariants({color, underline}),
          isButton && 'm-0 border-0 bg-transparent p-0 text-left',
          className,
        )}
        // Only meaningful on a `<button>` (an anchor's `type` is a MIME hint),
        // and spread before `props` so an explicit `type` still wins.
        {...(isButton ? {type: 'button' as const} : null)}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
Link.displayName = 'Link'
